import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { motion, useReducedMotion } from 'framer-motion'
import {
  AlertTriangle,
  CheckCircle2,
  CircleAlert,
  CircleHelp,
  LifeBuoy,
  Lightbulb,
  ListChecks,
  Loader2,
  Send,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import type { QueryClient } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { safeRandomUUID } from '@/lib/uuid'
import { apiFetch, getAccessToken } from '@/lib/api-client'
import { redactFeedbackMetadata } from '@/lib/redact'
import { captureFeedbackForSupport } from '@/lib/sentry'
import { FeedbackHistory } from '@/components/feedback/feedback-history'
import { FeedbackChat } from '@/components/feedback/feedback-chat'
import { ScreenshotEditor, SCREENSHOT_NOT_MASKED_COPY } from '@/components/feedback/screenshot-editor'
import {
  correlateFeedbackSentryEvent,
  FeedbackSubmitError,
  getFeedbackHistory,
  submitFeedback,
} from '@/services/feedback-api'
import type { FeedbackDeliveryResult, FeedbackHistoryItem, FeedbackReceipt } from '@/services/feedback-api'
import { useClientDiagnostics } from '@/hooks/useClientDiagnostics'
import type { DiagnosticsSnapshot } from '@/hooks/useClientDiagnostics'
import { useSupportModal } from '@/app/providers/support-modal-provider'
import type {
  ApplicationContextValue,
  ApplicationDiagnosticContext,
  SupportEntrySnapshot,
  SupportIntent,
} from '@/app/providers/support-modal-provider'
import { useSubscriptionContext } from '@/app/providers/subscription-provider'

type FeedbackCategory = 'bug' | 'improvement' | 'idea'

export type FeedbackFormValues = {
  intent?: SupportIntent
  category?: FeedbackCategory
  summary: string
  email?: string
}

export type SubmissionManifestField = {
  key: string
  name: string
  masked: boolean
  freeText: boolean
  value: unknown
}

export type SubmissionManifestItem = {
  id: string
  label: string
  included: boolean
  fields: SubmissionManifestField[]
}

type SupportModalProps = {
  open: boolean
  screenshot: string | null
  queryClient: QueryClient
  onClose: () => void
}

const DEFAULT_VALUES: FeedbackFormValues = {
  intent: 'problem',
  category: undefined,
  summary: '',
  email: '',
}

const FORM_FIELD_DEFINITIONS = {
  intent: { name: 'niyet', masked: false, freeText: false },
  category: { name: 'kategori', masked: false, freeText: false },
  summary: { name: 'başlık', masked: false, freeText: true },
  email: { name: 'oturum e-postası', masked: false, freeText: true },
} as const satisfies Record<
  keyof FeedbackFormValues,
  { name: string; masked: boolean; freeText: boolean }
>

export const FEEDBACK_FORM_VALUE_KEYS = Object.keys(
  FORM_FIELD_DEFINITIONS,
) as Array<keyof FeedbackFormValues>

export const TRANSPARENCY_DISCLOSURE =
  'Konsol ve ağ kayıtlarındaki e-posta, telefon, kart ve sipariş numarası, token ve adres sorgu parametreleri otomatik maskelenir; başlık ve oturum e-postası ise olduğu gibi iletilir.'

const TRANSPARENCY_PREFIX = 'Bu bildirimle gönderilen rapor içeriği: '
const DEFAULT_ENDPOINT = '/api/feedback/report'
const ABSOLUTE_URL_REGEX = /^https?:\/\//i

const AMBIGUOUS_COPY = "Gönderim sonucu doğrulanamadı. Aynı bildirimi yeniden göndermeden önce Bildirimlerim'i kontrol et."
const GENERAL_ERROR_COPY = 'Bildirim gönderilemedi. Yazdıkların korunuyor; bağlantını kontrol edip tekrar dene.'

function formatRetryDuration(seconds: number): string {
  return seconds < 60 ? `${seconds} saniye` : `${Math.ceil(seconds / 60)} dakika`
}

export function deliveryCopy(result: FeedbackDeliveryResult): string {
  if (result.attempted.length === 0) {
    return 'Bildirimin kaydedildi. Dış bildirim bu gönderim için uygulanmadı.'
  }
  if (result.unknown.length > 0) {
    return 'Bildirimin kaydedildi; bazı bildirim kanallarının sonucu henüz doğrulanamadı. Yeniden göndermene gerek yok.'
  }
  if (result.failed.length === result.attempted.length) {
    return 'Bildirimin kaydedildi; anlık bildirim gönderilemedi. Kaydın korunuyor ve sistem yeniden deneyecek.'
  }
  if (result.failed.length > 0) {
    return 'Bildirimin kaydedildi; ancak bazı bildirim kanallarına şu anda ulaşılamadı. Yeniden göndermene gerek yok.'
  }
  return 'Bildirim destek ekibine ulaştı.'
}

/**
 * Backend (`_feedback_eta_text`) tam cümle ("Tahmini dönüş süresi: 2 iş günü.") döndürür;
 * eski/test sözleşmesi ise yalnız değeri ("2 iş günü") verir. İki biçimi de tek, tekrarsız
 * satıra normalize eder (UAT-07: çift önek ve çift nokta görülmüştü).
 */
export function formatEtaLine(value: string | undefined): string {
  const trimmed = (value ?? '').trim().replace(/[.\s]+$/, '')
  if (!trimmed) return 'Tahmini dönüş süresi: bilinmiyor.'
  return /^tahmini dönüş süresi/i.test(trimmed) ? `${trimmed}.` : `Tahmini dönüş süresi: ${trimmed}.`
}

async function getFeedbackConfig(): Promise<{ estimatedResponse: string }> {
  const response = await apiFetch<{
    success: boolean
    data: { estimatedResponse: string }
  }>('/feedback/config')
  return response.data
}

type BuildSubmissionManifestArgs = {
  values: FeedbackFormValues
  diagnostics: unknown
  consoleLogs: unknown
  networkLogs: unknown
  screenshot: string | null
  includeScreenshot: boolean
}

type SubmissionApplicationContext = ApplicationDiagnosticContext & {
  applicationVersion: string
  commitSha: string
}

type SubmissionDiagnostics = DiagnosticsSnapshot & {
  applicationContext: SubmissionApplicationContext
}

function buildSubmissionDiagnostics(
  diagnostics: DiagnosticsSnapshot,
  applicationContext: ApplicationDiagnosticContext,
): SubmissionDiagnostics {
  return {
    ...diagnostics,
    applicationContext: {
      applicationVersion:
        typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'local',
      commitSha: typeof __COMMIT_SHA__ === 'string' ? __COMMIT_SHA__ : 'local',
      ...applicationContext,
    },
  }
}

export function buildSubmissionManifest({
  values,
  diagnostics,
  consoleLogs,
  networkLogs,
  screenshot,
  includeScreenshot,
}: BuildSubmissionManifestArgs): SubmissionManifestItem[] {
  const contact = values.email?.trim() ?? ''
  return [
    {
      id: 'form',
      label: 'form bilgileri',
      included: true,
      fields: [
        { key: 'intent', ...FORM_FIELD_DEFINITIONS.intent, value: values.intent ?? 'problem' },
        { key: 'category', ...FORM_FIELD_DEFINITIONS.category, value: values.category },
        { key: 'summary', ...FORM_FIELD_DEFINITIONS.summary, value: values.summary },
        { key: 'contact', ...FORM_FIELD_DEFINITIONS.email, value: contact },
        { key: 'email', ...FORM_FIELD_DEFINITIONS.email, value: contact },
      ],
    },
    {
      id: 'console-logs',
      label: 'konsol kayıtları',
      included: true,
      fields: [{ key: 'consoleLogs', name: 'konsol kayıtları', masked: true, freeText: false, value: consoleLogs }],
    },
    {
      id: 'network-logs',
      label: 'ağ kayıtları',
      included: true,
      fields: [{ key: 'networkLogs', name: 'ağ kayıtları', masked: true, freeText: false, value: networkLogs }],
    },
    {
      id: 'diagnostics',
      label: 'tarayıcı tanılaması',
      included: true,
      fields: [{ key: 'diagnostics', name: 'tarayıcı tanılaması', masked: true, freeText: false, value: diagnostics }],
    },
    {
      id: 'screenshot',
      label: 'ekran görüntüsü (maskelenmez)',
      included: Boolean(screenshot) && includeScreenshot,
      fields: [{ key: 'screenshot', name: 'ekran görüntüsü', masked: false, freeText: false, value: screenshot }],
    },
  ]
}

export function buildSubmissionPayload(manifest: SubmissionManifestItem[]): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  for (const item of manifest) {
    if (!item.included) continue
    for (const field of item.fields) {
      if (field.value !== undefined) payload[field.key] = field.value
    }
  }
  return payload
}

function buildTransparencyNote(manifest: SubmissionManifestItem[]): string {
  const includedLabels = manifest.filter((item) => item.included).map((item) => item.label)
  return `${TRANSPARENCY_PREFIX}${includedLabels.join(', ')}. ${TRANSPARENCY_DISCLOSURE}`
}

function resolveRequestTarget(input: string): {
  url: string
  credentials: RequestCredentials
  isSameOrigin: boolean
} {
  const normalized = input && input.trim().length > 0 ? input.trim() : DEFAULT_ENDPOINT
  if (ABSOLUTE_URL_REGEX.test(normalized)) {
    if (typeof window === 'undefined') return { url: normalized, credentials: 'include', isSameOrigin: true }
    const target = new URL(normalized)
    const sameOrigin = target.origin === window.location.origin
    return {
      url: target.toString(),
      credentials: sameOrigin ? 'include' : 'omit',
      isSameOrigin: sameOrigin,
    }
  }
  return {
    url: normalized.startsWith('/') ? normalized : `/${normalized}`,
    credentials: 'include',
    isSameOrigin: true,
  }
}

export const SUMMARY_MAX_CHARS = 200
export const DESCRIPTION_MAX_CHARS = 5000

const INTENTS = [
  { value: 'problem' as const, label: 'Sorun yaşıyorum', helper: 'Bir şey beklediğin gibi çalışmıyorsa bildir.', cta: 'Sorunu bildir', icon: CircleAlert },
  { value: 'how_to' as const, label: 'Nasıl yapılır', helper: 'Bir özelliği kullanmak için yardım iste.', cta: 'Yardım iste', icon: CircleHelp },
  { value: 'suggestion' as const, label: 'Öneri', helper: "Sensei'yi geliştirecek fikrini paylaş.", cta: 'Öneriyi gönder', icon: Lightbulb },
]

function snapshotApplicationContext(
  snapshot: SupportEntrySnapshot | null,
  fallback: ApplicationDiagnosticContext,
): ApplicationDiagnosticContext {
  if (!snapshot) return fallback
  return {
    plan: snapshot.plan,
    marketplace: snapshot.marketplace,
    dateRange: snapshot.dateRange,
    asin: snapshot.asin,
    lastFailedHttpStatus: snapshot.lastFailedHttpStatus,
    routeHistory: { status: 'active', value: [...snapshot.routeHistory] },
  }
}

function displayContextValue(value: ApplicationContextValue<unknown>): string | null {
  if (value.status === 'not_applicable') return null
  if (value.status === 'unknown') return 'Bilinmiyor'
  if (typeof value.value === 'string' || typeof value.value === 'number') return String(value.value)
  if (value.value && typeof value.value === 'object' && 'start' in value.value && 'end' in value.value) {
    return `${String(value.value.start)} – ${String(value.value.end)}`
  }
  return JSON.stringify(value.value)
}

function ContextSummary({ snapshot }: { snapshot: SupportEntrySnapshot | null }) {
  if (!snapshot) return null
  const rows = [
    { label: 'Ekran', value: snapshot.pathname },
    { label: 'Yakalanma zamanı', value: new Date(snapshot.capturedAt).toLocaleString('tr-TR') },
    { label: 'Marketplace', value: displayContextValue(snapshot.marketplace) },
    { label: 'Tarih aralığı', value: displayContextValue(snapshot.dateRange) },
    { label: 'ASIN', value: displayContextValue(snapshot.asin) },
    { label: 'Son API hata kodu', value: snapshot.lastFailedHttpStatus.status === 'active' ? `HTTP ${snapshot.lastFailedHttpStatus.value}` : displayContextValue(snapshot.lastFailedHttpStatus) },
  ].filter((row): row is { label: string; value: string } => row.value !== null)
  return (
    <section className="min-w-0 rounded-2xl border border-border bg-muted/40 p-4">
      <h3 className="text-base font-semibold">Bu bildirimle paylaşılacak bağlam</h3>
      <p className="mt-2 text-sm text-muted-foreground">Bu bilgiler bağlantıya tıkladığın anda kaydedildi ve değiştirilemez.</p>
      <dl className="mt-4 grid min-w-0 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-1">
        {rows.map((row) => (
          <div key={row.label} className="min-w-0">
            <dt className="text-xs font-semibold text-foreground">{row.label}</dt>
            <dd className="mt-1 break-words text-muted-foreground [overflow-wrap:anywhere]">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}


const HIDDEN_STYLE = { opacity: 0, visibility: 'hidden', transition: 'none' } as const

function computeChatHeight(): number {
  const viewport = typeof window === 'undefined' ? 800 : window.innerHeight
  return Math.min(720, Math.round(viewport * 0.92))
}

export function SupportModal({ open, screenshot, queryClient, onClose }: SupportModalProps) {
  const { consoleLogs, networkLogs, snapshotContext } = useClientDiagnostics(queryClient)
  const { applicationContext, entrySnapshot } = useSupportModal()
  const { sessionEmail } = useSubscriptionContext()
  const initialIntent = entrySnapshot?.source === 'sidebar' ? undefined : (entrySnapshot?.intent ?? 'problem')
  const [selectedIntent, setSelectedIntent] = useState<SupportIntent | undefined>(initialIntent)
  const [values, setValues] = useState<FeedbackFormValues>({ ...DEFAULT_VALUES, intent: initialIntent ?? 'problem', email: sessionEmail ?? '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState<{ type: 'error' | 'ambiguous'; message: string } | null>(null)
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null)
  const [receipt, setReceipt] = useState<FeedbackReceipt | null>(null)
  const [view, setView] = useState<'new' | 'history' | 'chat'>('new')
  const [chatTrackingId, setChatTrackingId] = useState<string | null>(null)
  const [windowHidden, setWindowHidden] = useState(false)
  const [chatHeight, setChatHeight] = useState(computeChatHeight)
  const reduceMotion = useReducedMotion()
  const [historyItems, setHistoryItems] = useState<FeedbackHistoryItem[]>([])
  const [isHistoryLoading, setIsHistoryLoading] = useState(false)
  const [isHistoryError, setIsHistoryError] = useState(false)
  const [includeScreenshot, setIncludeScreenshot] = useState(true)
  const [effectiveScreenshot, setEffectiveScreenshot] = useState(screenshot)
  const [eta, setEta] = useState<{ state: 'idle' | 'loading' | 'ready' | 'error'; value?: string }>({ state: 'idle' })
  const intentKeyRef = useRef(safeRandomUUID())
  const wasOpenRef = useRef(open)
  const firstIntentRef = useRef<HTMLButtonElement>(null)
  const formHeadingRef = useRef<HTMLHeadingElement>(null)
  const inFlightRef = useRef(false)

  useEffect(() => {
    const onResize = () => setChatHeight(computeChatHeight())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (retryAfterSeconds === null || retryAfterSeconds <= 0) return undefined
    const timeout = window.setTimeout(() => {
      setRetryAfterSeconds((current) => current === null ? null : Math.max(0, current - 1))
    }, 1000)
    return () => window.clearTimeout(timeout)
  }, [retryAfterSeconds])

  useEffect(() => {
    setValues((current) => ({ ...current, email: sessionEmail ?? '' }))
  }, [sessionEmail])

  useEffect(() => {
    setEffectiveScreenshot(screenshot)
  }, [screenshot])

  const diagnostics = useMemo(() => (open ? snapshotContext() : null), [open, snapshotContext])
  const submissionManifest = useMemo(() => buildSubmissionManifest({ values, diagnostics, consoleLogs, networkLogs, screenshot: effectiveScreenshot, includeScreenshot }), [consoleLogs, diagnostics, effectiveScreenshot, includeScreenshot, networkLogs, values])
  const transparencyNote = useMemo(() => buildTransparencyNote(submissionManifest), [submissionManifest])
  const endpoint = useMemo(() => typeof import.meta !== 'undefined' ? import.meta.env.VITE_FEEDBACK_ENDPOINT ?? DEFAULT_ENDPOINT : DEFAULT_ENDPOINT, [])

  const loadEta = useCallback(async () => {
    setEta({ state: 'loading' })
    try {
      const config = await getFeedbackConfig()
      setEta({ state: 'ready', value: config.estimatedResponse })
    } catch {
      setEta({ state: 'error' })
    }
  }, [])

  useEffect(() => {
    if (open && selectedIntent) void loadEta()
  }, [loadEta, open, selectedIntent])

  const loadHistory = useCallback(async () => {
    setIsHistoryLoading(true)
    setIsHistoryError(false)
    try { setHistoryItems(await getFeedbackHistory()) } catch { setIsHistoryError(true) } finally { setIsHistoryLoading(false) }
  }, [])

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      intentKeyRef.current = safeRandomUUID()
      const nextIntent = entrySnapshot?.source === 'sidebar' ? undefined : (entrySnapshot?.intent ?? 'problem')
      setSelectedIntent(nextIntent)
      setValues((current) => ({
        ...current,
        intent: nextIntent ?? 'problem',
        email: sessionEmail ?? '',
      }))
    }
    wasOpenRef.current = open
    if (!open) {
      const timeoutId = window.setTimeout(() => {
        setValues({ ...DEFAULT_VALUES, email: sessionEmail ?? '' }); setSelectedIntent(undefined); setStatus(null); setReceipt(null); setRetryAfterSeconds(null); setView('new'); setChatTrackingId(null); setWindowHidden(false); setHistoryItems([]); setIsHistoryError(false); setIsHistoryLoading(false); setIncludeScreenshot(true); setEffectiveScreenshot(screenshot); setEta({ state: 'idle' })
      }, 160)
      return () => window.clearTimeout(timeoutId)
    }
    return undefined
  }, [entrySnapshot, open, screenshot, sessionEmail])

  const selectIntent = useCallback((intent: SupportIntent) => {
    if (isSubmitting) return
    intentKeyRef.current = safeRandomUUID()
    setSelectedIntent(intent)
    setValues((current) => ({ ...current, intent }))
  }, [isSubmitting])

  const handleValueChange = useCallback(<Key extends keyof FeedbackFormValues>(key: Key, value: FeedbackFormValues[Key]) => {
    if (isSubmitting) return
    setValues((previous) => {
      if (previous[key] !== value) intentKeyRef.current = safeRandomUUID()
      return { ...previous, [key]: value }
    })
  }, [isSubmitting])

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    if (inFlightRef.current || !selectedIntent || !sessionEmail || (retryAfterSeconds !== null && retryAfterSeconds > 0)) return
    inFlightRef.current = true
    const submissionKey = intentKeyRef.current
    const submissionValues = { ...values, intent: selectedIntent, email: sessionEmail }
    setIsSubmitting(true)
    setStatus(null)
    try {
      const currentDiagnostics = buildSubmissionDiagnostics(snapshotContext(), snapshotApplicationContext(entrySnapshot, applicationContext))
      const metadata = buildSubmissionPayload(buildSubmissionManifest({ values: submissionValues, diagnostics: currentDiagnostics, consoleLogs, networkLogs, screenshot: effectiveScreenshot, includeScreenshot }))
      const redactedMetadata = redactFeedbackMetadata(metadata)
      const token = await getAccessToken()
      const { url, credentials, isSameOrigin } = resolveRequestTarget(endpoint)
      const nextReceipt = await submitFeedback({
        url,
        payload: redactedMetadata,
        idempotencyKey: submissionKey,
        token,
        credentials,
        includeAuthorization: isSameOrigin,
      })
      if (!nextReceipt.deduplicated && !nextReceipt.idempotentReplay) {
        try {
          const correlation = captureFeedbackForSupport({ message: submissionValues.summary })
          if (/^[0-9a-f]{32}$/.test(correlation.feedbackEventId ?? '')) {
            void correlateFeedbackSentryEvent(nextReceipt.trackingId, correlation.feedbackEventId!).catch(() => undefined)
          }
        } catch { /* best effort */ }
      }
      setReceipt(nextReceipt)
      setHistoryItems([])
      toast.success('Bildirimin kaydedildi')
    } catch (error) {
      if (error instanceof FeedbackSubmitError) {
        if (error.kind === 'auth') {
          setStatus({ type: 'error', message: 'Oturumun doğrulanamadı. Devam etmek için yeniden giriş yap.' })
        } else if (error.kind === 'rate-limit') {
          setRetryAfterSeconds(error.retryAfterSeconds ?? null)
          const duration = error.retryAfterSeconds ? formatRetryDuration(error.retryAfterSeconds) : null
          setStatus({ type: 'error', message: duration
            ? `Çok kısa sürede çok sayıda bildirim gönderdin. ${duration} sonra yeniden deneyebilirsin.`
            : 'Çok kısa sürede çok sayıda bildirim gönderdin. Bir süre sonra yeniden dene.' })
        } else if (error.kind === 'ambiguous') {
          setStatus({ type: 'ambiguous', message: AMBIGUOUS_COPY })
        } else {
          setStatus({ type: 'error', message: error.message === 'Feedback rejected' ? GENERAL_ERROR_COPY : error.message })
        }
      } else {
        setStatus({ type: 'ambiguous', message: AMBIGUOUS_COPY })
      }
    } finally {
      inFlightRef.current = false
      setIsSubmitting(false)
    }
  }

  const chatItem = view === 'chat' ? historyItems.find((entry) => entry.trackingId === chatTrackingId) ?? null : null
  const isChat = chatItem !== null
  const openChat = useCallback((trackingId: string) => {
    setChatTrackingId(trackingId)
    setView('chat')
  }, [])
  const closeChat = useCallback(() => {
    setView('history')
    setChatTrackingId(null)
    void loadHistory()
  }, [loadHistory])
  const markChatRead = useCallback((trackingId: string) => {
    setHistoryItems((items) => items.map((entry) => (entry.trackingId === trackingId ? { ...entry, chatUnread: 0 } : entry)))
  }, [])

  const intentDefinition = INTENTS.find((intent) => intent.value === selectedIntent)
  const showPicker = view === 'new' && !selectedIntent

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => (!nextOpen && !isSubmitting ? onClose() : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay data-support-modal="true" style={windowHidden ? HIDDEN_STYLE : undefined} className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out motion-reduce:animate-none" />
        <Dialog.Content
          asChild
          data-support-modal="true"
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            if (entrySnapshot?.source === 'sidebar') firstIntentRef.current?.focus()
            else formHeadingRef.current?.focus()
          }}
        >
        <motion.div
          initial={false}
          animate={{ height: isChat ? chatHeight : 'auto' }}
          transition={reduceMotion ? { duration: 0 } : { duration: 0.22, ease: 'easeOut' }}
          style={windowHidden ? HIDDEN_STYLE : undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 max-h-[92vh] -translate-x-1/2 -translate-y-1/2 overflow-x-hidden rounded-3xl border border-border bg-card text-foreground shadow-2xl transition-[width] duration-200 ease-out focus:outline-none motion-reduce:animate-none motion-reduce:transition-none',
            isChat ? 'w-[min(96vw,1180px)] overflow-y-hidden p-0' : 'w-[min(96vw,1080px)] overflow-y-auto p-4 sm:p-6',
          )}
        >
          {isChat ? (
            <>
              <Dialog.Title className="sr-only">Yardım ve Destek: sohbet</Dialog.Title>
              <Dialog.Description className="sr-only">Destek ekibiyle bu bildirim hakkında yazışabilirsin.</Dialog.Description>
              <FeedbackChat
                key={chatItem.trackingId}
                item={chatItem}
                queryClient={queryClient}
                onBack={closeChat}
                onRead={markChatRead}
                onWindowHiddenChange={setWindowHidden}
                closeButton={<Dialog.Close asChild><button type="button" className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2" aria-label="Yardım ve Destek penceresini kapat"><X className="size-5" /></button></Dialog.Close>}
              />
            </>
          ) : (
            <>
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3"><span className="inline-flex size-12 shrink-0 items-center justify-center rounded-2xl bg-muted text-brand-purple"><LifeBuoy className="size-6" aria-hidden="true" /></span><div className="min-w-0"><Dialog.Title className="text-xl font-semibold leading-tight">Yardım ve Destek</Dialog.Title><Dialog.Description className="mt-1 text-sm text-muted-foreground">Sorun bildir, kullanım desteği al veya önerini paylaş.</Dialog.Description></div></div>
            <Dialog.Close asChild><button type="button" disabled={isSubmitting} className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2" aria-label="Yardım ve Destek penceresini kapat"><X className="size-5" /></button></Dialog.Close>
          </div>
          <div className="mt-5 flex flex-wrap gap-2" aria-label="Destek görünümü">
            <button type="button" disabled={isSubmitting} aria-pressed={view === 'new'} onClick={() => setView('new')} className={cn('min-h-11 rounded-full border px-4 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2', view === 'new' ? 'border-brand-purple bg-brand-purple/10 text-brand-purple' : 'border-border text-muted-foreground')}>Yeni bildirim</button>
            <button type="button" disabled={isSubmitting} aria-pressed={view === 'history'} onClick={() => { setView('history'); void loadHistory() }} className={cn('inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2', view === 'history' ? 'border-brand-purple bg-brand-purple/10 text-brand-purple' : 'border-border text-muted-foreground')}><ListChecks className="size-4" aria-hidden="true" />Bildirimlerim</button>
          </div>
          {view === 'history' || view === 'chat' ? (
            <section className="mt-6" aria-label="Bildirimlerim"><FeedbackHistory items={historyItems} isLoading={isHistoryLoading} isError={isHistoryError} onRetry={() => void loadHistory()} onStartNew={() => setView('new')} onOpenChat={openChat} /></section>
          ) : showPicker ? (
            <section className="mt-6"><h3 className="text-base font-semibold">Nasıl yardımcı olabiliriz?</h3><div className="mt-4 grid gap-4 sm:grid-cols-3">{INTENTS.map(({ value, label, helper, icon: Icon }) => <button key={value} ref={value === 'problem' ? firstIntentRef : undefined} type="button" onClick={() => selectIntent(value)} className="min-h-24 rounded-2xl border border-border p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2"><Icon className="size-5 text-brand-purple" aria-hidden="true" /><span className="mt-3 block text-base font-semibold">{label}</span><span className="mt-2 block text-sm text-muted-foreground">{helper}</span></button>)}</div></section>
          ) : (
            <div className="mt-6 grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <form onSubmit={handleSubmit} aria-busy={isSubmitting} className="min-w-0 space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-2"><div><h3 ref={formHeadingRef} tabIndex={-1} className="text-base font-semibold">{intentDefinition?.label}</h3>{eta.state === 'loading' ? <p role="status" className="mt-2 text-sm text-muted-foreground">Tahmini dönüş süresi yükleniyor.</p> : null}{eta.state === 'ready' ? <p className="mt-2 text-sm text-muted-foreground">{formatEtaLine(eta.value)}</p> : null}{eta.state === 'error' ? <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-destructive"><span>Tahmini dönüş süresi alınamadı.</span><button type="button" className="min-h-11 underline" onClick={() => void loadEta()}>Yeniden dene</button></div> : null}</div><button type="button" disabled={isSubmitting} onClick={() => setSelectedIntent(undefined)} className="min-h-11 text-sm text-brand-purple underline">Niyeti değiştir</button></div>
                <div className="space-y-2 text-sm"><label htmlFor="feedback-summary" className="block font-medium">Ne oldu?</label><Textarea id="feedback-summary" placeholder="Karşılaştığın durumu kısaca anlat." value={values.summary} disabled={isSubmitting} onChange={(event) => handleValueChange('summary', event.target.value)} required rows={5} aria-describedby="feedback-summary-warning" maxLength={SUMMARY_MAX_CHARS} className="rounded-2xl" /><p id="feedback-summary-warning" className="text-xs leading-relaxed text-muted-foreground">Bu alan olduğu gibi iletilir — şifre, API anahtarı veya kart bilgisi yazmayın.</p></div>
                <div className="space-y-2 text-sm"><label htmlFor="feedback-email" className="block font-medium">E-posta</label><Input id="feedback-email" type="email" value={sessionEmail ?? ''} readOnly aria-readonly="true" className="h-auto min-h-11 whitespace-normal break-all rounded-2xl bg-muted" /><span className="block text-xs text-muted-foreground">Yanıtı bu oturum e-postasına göndereceğiz.</span></div>
                <label className="block space-y-2 text-sm"><span className="font-medium">Kategori (isteğe bağlı)</span><select value={values.category ?? ''} disabled={isSubmitting} onChange={(event) => handleValueChange('category', (event.target.value || undefined) as FeedbackCategory | undefined)} className="min-h-11 w-full rounded-2xl border border-border bg-card px-3"><option value="">Seçim yok</option><option value="bug">Hata</option><option value="improvement">İyileştirme</option><option value="idea">Fikir</option></select></label>
                {!sessionEmail ? <div role="alert" className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"><p>Oturumun doğrulanamadı. Devam etmek için yeniden giriş yap.</p><a href="/login" className="mt-2 inline-flex min-h-11 items-center underline">Yeniden giriş yap</a></div> : null}
                {receipt ? <section role="status" aria-label="Gönderim makbuzu" className="min-w-0 rounded-2xl border border-success/30 bg-success/10 p-4"><p className="flex items-center gap-2 font-medium"><CheckCircle2 className="size-5 text-success" aria-hidden="true" />Bildirimin kaydedildi</p><dl className="mt-3 grid min-w-0 gap-2 text-sm sm:grid-cols-2"><div className="min-w-0"><dt className="text-xs text-muted-foreground">Takip numarası</dt><dd className="mt-1 break-all font-mono text-xs font-semibold">{receipt.trackingId}</dd></div><div className="min-w-0"><dt className="text-xs text-muted-foreground">Tahmini yanıt</dt><dd className="mt-1 break-words">{receipt.estimatedResponse}</dd></div></dl><div className="mt-4 min-w-0 border-t border-success/20 pt-3"><p className="text-sm font-semibold">Destek ekibine bildirim</p><p className="mt-2 flex min-w-0 items-start gap-2 break-words text-sm text-muted-foreground"><span aria-hidden="true">{receipt.deliveryResult.attempted.length === 0 ? '—' : receipt.deliveryResult.failed.length > 0 || receipt.deliveryResult.unknown.length > 0 ? '⚠' : '✓'}</span><span className="min-w-0 break-words">{deliveryCopy(receipt.deliveryResult)}</span></p></div></section> : null}
                {status ? <div id="feedback-submit-status" role={status.type === 'error' ? 'alert' : 'status'} className={cn('min-w-0 rounded-2xl p-3 text-sm', status.type === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-warning-foreground')}><p className="flex min-w-0 items-start gap-2 break-words"><AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" /><span className="min-w-0 break-words">{status.message}</span></p>{status.type === 'ambiguous' ? <button type="button" className="mt-2 min-h-11 text-brand-purple underline" onClick={() => { setView('history'); void loadHistory() }}>Bildirimlerimi kontrol et</button> : null}</div> : null}
                <div className="flex flex-wrap justify-end gap-3"><Button type="submit" aria-describedby={status?.type === 'error' ? 'feedback-submit-status' : undefined} className="min-h-11 max-w-full whitespace-normal rounded-full px-5" disabled={isSubmitting || !sessionEmail || (retryAfterSeconds !== null && retryAfterSeconds > 0)}>{isSubmitting ? <Loader2 className="size-4 animate-spin motion-reduce:animate-none" /> : <Send className="size-4" />}{isSubmitting ? 'Gönderiliyor…' : retryAfterSeconds !== null && retryAfterSeconds > 0 ? `${formatRetryDuration(retryAfterSeconds)} sonra yeniden dene` : intentDefinition?.cta}</Button></div>
                <p data-testid="submission-transparency" className="text-xs leading-relaxed text-muted-foreground">{transparencyNote}</p>
              </form>
              <aside className="min-w-0 space-y-4">
                <ContextSummary snapshot={entrySnapshot} />
                {effectiveScreenshot ? (
                  <ScreenshotEditor
                    key={screenshot ?? 'screenshot'}
                    source={effectiveScreenshot}
                    originalSource={screenshot ?? effectiveScreenshot}
                    included={includeScreenshot}
                    isSubmitting={isSubmitting}
                    isCropped={effectiveScreenshot !== screenshot}
                    onIncludedChange={() => setIncludeScreenshot((included) => !included)}
                    onSourceChange={setEffectiveScreenshot}
                    onReset={() => setEffectiveScreenshot(screenshot)}
                  />
                ) : (
                  <section className="rounded-2xl border border-border bg-muted/40 p-4">
                    <p className="text-base font-semibold">Ekran görüntüsü</p>
                    <div className="mt-3 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground"><p>Ekran görüntüsü alınamadı.</p><p className="mt-2">Bildirimi ekran görüntüsü olmadan gönderebilirsin.</p></div>
                    <p className="mt-3 text-xs leading-relaxed text-warning-foreground">{SCREENSHOT_NOT_MASKED_COPY}</p>
                  </section>
                )}
              </aside>
            </div>
          )}
            </>
          )}
        </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
